import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldError } from '@/components/ui/field-error';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminFormDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    password: '',
    role: 'accountant' as 'accountant' | 'superadmin',
  });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/admin/admins', {
        method: 'POST',
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admins'] });
      toast.success(`Admin creado: ${form.fullName}`);
      onOpenChange(false);
      setForm({ email: '', fullName: '', password: '', role: 'accountant' });
      setIssues({});
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.body.error === 'email_already_exists') {
          setIssues({ email: ['Ya existe un admin con ese email'] });
        } else if (err.body.error === 'forbidden') {
          setIssues({ _form: ['Solo un superadmin puede crear admins'] });
        } else if (err.body.issues) {
          setIssues(err.body.issues);
        } else {
          setIssues({ _form: [err.body.error] });
        }
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo admin</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fullName" required>
              Nombre completo
            </Label>
            <Input
              id="fullName"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              required
            />
            <FieldError error={issues.fullName} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" required>
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <FieldError error={issues.email} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" required>
              Contraseña (mín 8)
            </Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
            />
            <FieldError error={issues.password} />
          </div>
          <div className="space-y-1.5">
            <Label>Rol</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v as 'accountant' | 'superadmin' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accountant">Contador</SelectItem>
                <SelectItem value="superadmin">Superadmin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {issues._form && <p className="text-sm text-destructive">{issues._form[0]}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creando…' : 'Crear admin'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
